/**
 * Shared chrome and formatting for the mailing screens.
 *
 * Every screen renders the same sub-navigation and the same inline feedback
 * banner, so both live here rather than being copy-pasted seven times.
 */
define('mailingShell',[
  'module',
  'underscore',
  'jquery'
], function (
  module,
  _,
  $
) {
  'use strict';

  var TABS = [
    { key: 'campaigns', route: 'mailing/campaigns', label: 'Campaigns' },
    { key: 'personalised', route: 'mailing/personalised', label: 'Personalised' },
    { key: 'workflows', route: 'mailing/workflows', label: 'Workflows' },
    { key: 'templates', route: 'mailing/templates', label: 'Templates' },
    { key: 'segments', route: 'mailing/segments', label: 'Segments' },
    { key: 'subscribers', route: 'mailing/subscribers', label: 'Contacts' },
    { key: 'queue', route: 'mailing/queue', label: 'Outbox' },
    { key: 'settings', route: 'mailing/settings', label: 'Settings' }
  ];

  var Shell = {

    /**
     * Sub-navigation bar. `active` is one of the tab keys; `actionsHtml` is
     * dropped on the right of the bar for screen-specific buttons.
     */
    navHtml: function (active, actionsHtml) {
      var tabs = _.map(TABS, function (tab) {
        return '<a class="mlg-tab' + (tab.key === active ? ' active' : '') +
          '" href="#' + tab.route + '">' + _.escape(tab.label) + '</a>';
      }).join('');
      return '<div class="mlg-nav">' +
        '<div class="mlg-tabs">' + tabs + '</div>' +
        '<div class="mlg-nav-actions">' + (actionsHtml || '') + '</div>' +
        '</div>' +
        '<div class="mlg-banner" style="display:none"></div>';
    },

    /** Inline feedback. `type` is 'ok' or 'error'; both auto-dismiss. */
    banner: function ($root, message, type) {
      var $banner = $root.find('.mlg-banner').first();
      if (!$banner.length) { return; }
      $banner
        .removeClass('ok error')
        .addClass(type === 'error' ? 'error' : 'ok')
        .text(message)
        .show();
      if ($banner.data('mlgTimer')) {
        window.clearTimeout($banner.data('mlgTimer'));
      }
      $banner.data('mlgTimer', window.setTimeout(function () { $banner.fadeOut(200); },
        type === 'error' ? 8000 : 4000));
    },

    /** Day-first date, the format used across this dashboard. */
    formatDate: function (value, withTime) {
      if (!value) { return ''; }
      var date = new Date(value);
      if (isNaN(date.getTime())) { return ''; }
      var options = { day: '2-digit', month: '2-digit', year: 'numeric' };
      if (withTime) {
        options.hour = '2-digit';
        options.minute = '2-digit';
      }
      return date.toLocaleString('fr-BE', options);
    },

    /** Thousands separator without locale APIs (the jshint target is es3). */
    formatNumber: function (value) {
      var rounded = Math.round(Number(value) || 0);
      return String(rounded).replace(/\B(?=(\d{3})+(?!\d))/g, ' ');
    },

    formatMoney: function (value) {
      var amount = Number(value) || 0;
      return '€ ' + Shell.formatNumber(amount);
    },

    formatPercent: function (ratio) {
      return (Math.round((Number(ratio) || 0) * 1000) / 10) + '%';
    },

    /** Turns a backend CONSTANT into a readable label. */
    prettyCase: function (value) {
      if (!value) { return ''; }
      return String(value).replace(/_/g, ' ').toLowerCase()
        .replace(/\b([a-z])/g, function (match, character) { return character.toUpperCase(); });
    },

    /** Datetime-local input value ("2026-08-17T14:30") from a Date. */
    toLocalInputValue: function (date) {
      function pad(n) { return (n < 10 ? '0' : '') + n; }
      return date.getFullYear() + '-' + pad(date.getMonth() + 1) + '-' + pad(date.getDate()) +
        'T' + pad(date.getHours()) + ':' + pad(date.getMinutes());
    },

    /**
     * Sending window: the days and hours emails may leave. Days are ISO weekdays
     * (1 = Monday … 7 = Sunday); times are "HH:mm" in the account's mailing time
     * zone (Mailing → Settings; the backend sends it along as `sendWindowTimeZone`).
     * A campaign without its own window uses this default.
     */
    DEFAULT_SEND_WINDOW: { sendDays: [1, 2, 3, 4, 5], sendStartTime: '09:00', sendEndTime: '17:30' },

    sendWindowOf: function (model) {
      var fallback = this.DEFAULT_SEND_WINDOW;
      var days = model && _.isArray(model.sendDays) && model.sendDays.length ? model.sendDays : fallback.sendDays;
      return {
        sendDays: _.map(days, Number),
        sendStartTime: (model && model.sendStartTime) || fallback.sendStartTime,
        sendEndTime: (model && model.sendEndTime) || fallback.sendEndTime
      };
    },

    /** "Mon–Fri, 09:00–17:30" */
    sendWindowSummary: function (model) {
      var names = ['', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'];
      var window_ = this.sendWindowOf(model);
      var days = _.sortBy(window_.sendDays);
      var consecutive = days.length > 2 && days[days.length - 1] - days[0] === days.length - 1;
      return (consecutive ? names[days[0]] + '–' + names[days[days.length - 1]] :
          _.map(days, function (d) { return names[d]; }).join(', ')) +
        ', ' + window_.sendStartTime + '–' + window_.sendEndTime;
    },

    /** Form fields for the sending window, prefilled from `model` (default: Mon–Fri 09:00–17:30). */
    sendWindowHtml: function (model) {
      var window_ = this.sendWindowOf(model);
      var names = ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'];
      var days = _.map(names, function (name, index) {
        var day = index + 1;
        return '<label class="mlg-day"><input type="checkbox" class="f-send-day" value="' + day + '"' +
          (_.contains(window_.sendDays, day) ? ' checked' : '') + '> ' + name + '</label>';
      }).join('');
      return '<div class="mlg-send-window">' +
        '<div class="mlg-days">' + days + '</div>' +
        '<div class="mlg-row">' +
        '  <label>Start sending at<input type="time" class="f-send-start" value="' + window_.sendStartTime + '"></label>' +
        '  <label>Stop sending at<input type="time" class="f-send-end" value="' + window_.sendEndTime + '"></label>' +
        '</div>' +
        '<div class="mlg-hint">Emails only leave on these days and between these hours (' +
          _.escape((model && model.sendWindowTimeZone) || 'mailing time zone, see Settings') + '). ' +
          'Whatever is left waits for the next window. Default: Mon–Fri, 09:00–17:30.</div>' +
        '</div>';
    },

    /** Reads the fields of sendWindowHtml. Returns { data } or { error }. */
    readSendWindow: function ($root) {
      var days = _.map($root.find('.f-send-day:checked'), function (input) { return Number(input.value); });
      var start = $root.find('.f-send-start').val();
      var end = $root.find('.f-send-end').val();
      if (!days.length) { return { error: 'Pick at least one sending day' }; }
      if (!start || !end) { return { error: 'Set the start and stop time of the sending window' }; }
      if (start >= end) { return { error: 'The stop time must be after the start time' }; }
      return { data: { sendDays: days, sendStartTime: start, sendEndTime: end } };
    },

    /**
     * Open / click rates (and unsubscribe / bounce counts when there are any)
     * next to a campaign in a list. Rates are recipients over emails sent.
     * Empty until something was actually sent.
     */
    /**
     * Editor of a personalised campaign's image prompt: the instructions the AI
     * gets for every image. Wire it with bindImagePrompt, read it with
     * $root.find('.f-image-prompt').val().
     */
    imagePromptHtml: function () {
      return '<div class="mlg-image-prompt">' +
        '<div class="mlg-row"><span class="grow mlg-image-prompt-state">Built-in prompt</span>' +
        '  <button type="button" class="mlg-btn small mlg-image-prompt-reset">Reset to built-in</button></div>' +
        '<textarea class="f-image-prompt" rows="14" spellcheck="false" placeholder="Loading the built-in prompt…">' +
        '</textarea>' +
        '<div class="mlg-hint">Instructions the AI gets for every image of this campaign. After them it always ' +
          'receives the recipient\'s website, the parameters above (product, occasion, details, mandatory text, ' +
          'language), the product photo and the logo. Left as is, the campaign follows the built-in prompt — ' +
          'and its future improvements.</div>' +
        '</div>';
    },

    /** Fills the editor with the campaign's prompt (else the built-in one) and keeps its state label right. */
    bindImagePrompt: function ($root, current, builtIn) {
      var $text = $root.find('.f-image-prompt');
      var $state = $root.find('.mlg-image-prompt-state');
      var $reset = $root.find('.mlg-image-prompt-reset');
      var normalise = function (value) { return $.trim(String(value || '').replace(/\r\n?/g, '\n')); };
      var refresh = function () {
        var custom = normalise($text.val()) !== '' && normalise($text.val()) !== normalise(builtIn);
        $state.text(custom ? 'Custom prompt — this campaign only' : 'Built-in prompt');
        $reset.prop('disabled', !custom);
      };
      $text.attr('placeholder', '').val(current || builtIn || '');
      $text.off('input.mlgPrompt').on('input.mlgPrompt', refresh);
      $reset.off('click.mlgPrompt').on('click.mlgPrompt', function () {
        $text.val(builtIn || '');
        refresh();
      });
      refresh();
    },

    statsPillsHtml: function (row) {
      var sent = Number(row.statSent) || 0;
      if (!sent) { return ''; }
      var opens = Number(row.statOpens) || 0;
      var clicks = Number(row.statClicks) || 0;
      var unsubs = Number(row.statUnsubs) || 0;
      var bounced = Number(row.statBounced) || 0;
      return '<div class="mlg-pills">' +
        '<span class="mlg-pill opens" title="' + opens + ' of ' + sent + ' recipients opened">' +
          Shell.formatPercent(opens / sent) + ' opened</span>' +
        '<span class="mlg-pill clicks" title="' + clicks + ' of ' + sent + ' recipients clicked">' +
          Shell.formatPercent(clicks / sent) + ' clicked</span>' +
        (unsubs ? '<span class="mlg-pill unsubs" title="Unsubscribed">' + unsubs + ' unsub.</span>' : '') +
        (bounced ? '<span class="mlg-pill bounced" title="Could not be delivered">' + bounced + ' failed</span>' : '') +
        '</div>';
    },

    /**
     * Shows rendered email HTML in a sandboxed iframe. `srcdoc` rather than a
     * URL: the HTML comes from an authenticated call, and an iframe URL would
     * need the session token in it. No `allow-scripts` — a template is markup.
     */
    showEmailHtml: function ($host, html) {
      var iframe = document.createElement('iframe');
      iframe.className = 'mlg-email-frame';
      // Still no script and no access to the dashboard, but the links work — the
      // proofsheet button included — and open in a new tab, as from a mail client.
      iframe.setAttribute('sandbox', 'allow-popups allow-popups-to-escape-sandbox');
      iframe.setAttribute('srcdoc', this.linksInNewTab(html || ''));
      $host.empty().append(iframe);
    },

    /** Makes every link of an email open in a new tab (a base target, unless the email sets one). */
    linksInNewTab: function (html) {
      if (/<base\b/i.test(html)) {
        return html;
      }
      var base = '<base target="_blank">';
      if (/<head[^>]*>/i.test(html)) {
        return html.replace(/<head[^>]*>/i, function (head) { return head + base; });
      }
      return base + html;
    },

    /**
     * Copies text to the clipboard; resolves true / false. The async API needs a
     * secure context, so plain-http dev setups go through the old
     * select-and-execCommand route.
     */
    copyText: function (text) {
      var defer = $.Deferred();
      function legacy() {
        var $area = $('<textarea readonly style="position:fixed;top:0;left:0;opacity:0"></textarea>')
          .val(text).appendTo(document.body);
        var copied = false;
        try {
          $area[0].select();
          copied = document.execCommand('copy');
        } catch (error) {
          copied = false;
        }
        $area.remove();
        defer.resolve(copied);
      }
      if (window.navigator.clipboard && window.navigator.clipboard.writeText) {
        window.navigator.clipboard.writeText(text).then(function () { defer.resolve(true); }, legacy);
      } else {
        legacy();
      }
      return defer.promise();
    },

    /**
     * Thumbnail of a file-servlet image: the servlet resizes on
     * `?action=res&params=t`, so a list of 500 generated images does not pull
     * 500 full-size PNGs. Anything that is not a servlet URL (a data URI, a
     * foreign host) is returned as it is — the servlet is the only thing that
     * understands the parameters.
     */
    thumbnailUrl: function (url) {
      if (!url || !/\/getfile\//i.test(url) || /[?&]action=/i.test(url)) { return url; }
      return url + (url.indexOf('?') === -1 ? '?' : '&') + 'action=res&params=t';
    },

    /** Small "no rows yet" block, so empty states look the same everywhere. */
    emptyHtml: function (message) {
      return '<div class="mlg-empty">' + _.escape(message) + '</div>';
    }
  };

  module.exports = Shell;
});

